// Invoice validation utilities

export interface ValidationError {
  field: string
  message: string
  severity: 'error' | 'warning'
  rule?: string
}

export interface ValidationResult {
  isValid: boolean
  errors: ValidationError[]
  warnings: ValidationError[]
}

export function validateInvoice(
  invoice: any,
  documentType?: string,
): ValidationResult {
  const errors: ValidationError[] = []
  const warnings: ValidationError[] = []

  // Check required fields
  if (!invoice.invoiceNumber) {
    errors.push({
      field: 'invoiceNumber',
      message: 'Invoice number is required',
      severity: 'error',
      rule: 'Required Field',
    })
  }

  if (!invoice.supplier) {
    errors.push({
      field: 'supplier',
      message: 'Supplier is required',
      severity: 'error',
      rule: 'Required Field',
    })
  }

  if (!invoice.invoiceDate) {
    errors.push({
      field: 'invoiceDate',
      message: 'Invoice date is required',
      severity: 'error',
      rule: 'Required Field',
    })
  }

  // Check amounts
  if (invoice.total <= 0 && documentType !== 'Credit Note') {
    warnings.push({
      field: 'total',
      message: 'Invoice total is zero or negative',
      severity: 'warning',
      rule: 'Amount Check',
    })
  }

  // Check line items
  if (!invoice.lineItems || invoice.lineItems.length === 0) {
    errors.push({
      field: 'lineItems',
      message: 'At least one line item is required',
      severity: 'error',
      rule: 'Required Field',
    })
  }

  // Validate tax codes on line items
  if (invoice.lineItems) {
    invoice.lineItems.forEach((item: any, index: number) => {
      if (!item.taxCode) {
        warnings.push({
          field: 'taxCode',
          message: `Line ${index + 1}: Tax code is missing`,
          severity: 'warning',
          rule: 'Tax Code Required',
        })
      }

      // Tax Code E Rule - Exclusive means no tax at line level
      if (item.taxCode === 'E' && item.taxAmt !== 0) {
        errors.push({
          field: 'taxAmt',
          message: `Line ${index + 1}: Tax code "E" (Exclusive) means tax is NOT shown at line level. Tax amount must be $ 0.00, got $ ${Math.abs(item.taxAmt).toFixed(2)}`,
          severity: 'error',
          rule: 'Tax Code E Rule',
        })
      }

      // Tax Rate Check
      if (item.taxCode === 'E' && item.taxAmt !== 0) {
        errors.push({
          field: 'taxAmt',
          message: `Line ${index + 1}: Tax does not match rate for tax code "E". Expected $ 0.00, got $ ${Math.abs(item.taxAmt).toFixed(2)}`,
          severity: 'error',
          rule: `Rule ${index + 1}: Tax Rate Check`,
        })
      }

      // Invalid Tax Amount for E code
      if (item.taxCode === 'E' && item.taxAmt !== 0) {
        errors.push({
          field: 'taxCode',
          message: `Line ${index + 1}: Tax code "E" requires zero tax at line level. Tax amount must be $ 0.00, but got $ ${Math.abs(item.taxAmt).toFixed(2)}`,
          severity: 'error',
          rule: `Rule ${index + 1}/8: Invalid Tax Amount`,
        })
      }

      // GL Code validation
      if (!item.glCode) {
        errors.push({
          field: 'glCode',
          message: `Line ${index + 1}: GL Code is required for all line items`,
          severity: 'error',
          rule: 'GL Code Required',
        })
      }

      // Description check
      if (!item.description || item.description.trim().length < 3) {
        warnings.push({
          field: 'description',
          message: `Line ${index + 1}: Description is too short or missing`,
          severity: 'warning',
          rule: 'Description Check',
        })
      }
    })
  }

  // Subtotal verification
  if (invoice.lineItems && invoice.lineItems.length > 0) {
    const calculatedSubtotal = invoice.lineItems.reduce(
      (sum: number, item: any) => sum + (item.subtotal || 0),
      0,
    )
    const diff = Math.abs(calculatedSubtotal - invoice.subtotal)
    if (diff > 0.02) {
      errors.push({
        field: 'subtotal',
        message: `Subtotal mismatch: sum of line items is $ ${calculatedSubtotal.toFixed(2)}, but header subtotal is $ ${invoice.subtotal.toFixed(2)}`,
        severity: 'error',
        rule: 'Subtotal Verification',
      })
    }
  }

  // Tax total verification
  if (invoice.lineItems && invoice.lineItems.length > 0) {
    const calculatedTax = invoice.lineItems.reduce(
      (sum: number, item: any) =>
        sum + (typeof item.taxAmt === 'number' ? item.taxAmt : 0),
      0,
    )
    const diff = Math.abs(calculatedTax - invoice.taxAmt)
    if (diff > 0.02) {
      errors.push({
        field: 'taxAmt',
        message: `Tax total mismatch: sum of line taxes is $ ${calculatedTax.toFixed(2)}, but header tax is $ ${invoice.taxAmt.toFixed(2)}`,
        severity: 'error',
        rule: 'Tax Total Verification',
      })
    }
  }

  // PO Number check for PO matching types
  if (
    (invoice.matchingType === '2-way' || invoice.matchingType === '3-way') &&
    !invoice.poNumber
  ) {
    errors.push({
      field: 'poNumber',
      message: `PO Number is required for ${invoice.matchingType} matching`,
      severity: 'error',
      rule: 'PO Matching Rule',
    })
  }

  // Vendor bank check
  if (
    invoice.supplierABN &&
    !invoice.supplierABN.match(/^\d{2}\s\d{3}\s\d{3}\s\d{3}$/)
  ) {
    warnings.push({
      field: 'supplierABN',
      message: 'Supplier ABN format may be invalid',
      severity: 'warning',
      rule: 'ABN Format Check',
    })
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  }
}

export function validateLineItem(lineItem: any): ValidationError[] {
  const errors: ValidationError[] = []

  if (!lineItem.description) {
    errors.push({
      field: 'description',
      message: 'Description is required',
      severity: 'error',
      rule: 'Required Field',
    })
  }

  if (lineItem.quantity <= 0) {
    errors.push({
      field: 'quantity',
      message: 'Quantity must be greater than zero',
      severity: 'error',
      rule: 'Quantity Check',
    })
  }

  if (lineItem.unitPrice < 0) {
    errors.push({
      field: 'unitPrice',
      message: 'Unit price cannot be negative',
      severity: 'error',
      rule: 'Price Check',
    })
  }

  return errors
}
